import { CbhIcon } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { type FileMessage, type UserMessage } from "@sendbird/chat/message";
import { type EveryMessage } from "@sendbird/uikit-react/types/types";
import { type CoreMessageType } from "@sendbird/uikit-react/types/utils";
import { useGetWorkerPublicProfile } from "@src/appV2/redesign/WorkWithFriends/hooks/useGetWorkerPublicProfile";

import { CustomMessageTypes } from "../type";
import { type ChatChannel } from "../types";
import {
  isWorkplaceGroupChannel,
  parseAgentChatMessage,
  parseShiftInviteChatMessage,
  shiftTimeProposalDisplayMessage,
} from "../utils";
import { getSendHomeRequestMessage } from "../utils/getSendHomeRequestMessage";

interface LastMessagePreviewProps {
  channel: ChatChannel;
  currentUserId?: string;
}

function getFirstNameFromNickname(nickname?: string): string {
  if (!isDefined(nickname)) {
    return "";
  }

  return nickname.split(" ")[0];
}

export function LastMessagePreview(props: LastMessagePreviewProps) {
  const { channel, currentUserId } = props;
  const { lastMessage } = channel;

  const nonAdminLastMessage = lastMessage as unknown as UserMessage | FileMessage;
  const senderUserId = nonAdminLastMessage?.sender?.userId;

  const isOutgoingMessage =
    isDefined(currentUserId) && isDefined(senderUserId) && senderUserId === currentUserId;
  const isWorkplaceGroupChat = isWorkplaceGroupChannel(channel.url);
  const shouldFetchSenderProfile =
    isWorkplaceGroupChat && !isOutgoingMessage && isDefined(senderUserId) && isDefined(lastMessage);

  const { data: senderProfile } = useGetWorkerPublicProfile(senderUserId ?? "", {
    enabled: shouldFetchSenderProfile,
  });

  if (!isDefined(lastMessage)) {
    return <>No messages</>;
  }

  if (lastMessage.customType === CustomMessageTypes.SHIFT_BOOKED) {
    return <>{parseAgentChatMessage(lastMessage as CoreMessageType)}</>;
  }

  if (
    [
      CustomMessageTypes.SHIFT_INVITE_REQUEST,
      CustomMessageTypes.SHIFT_INVITE_RESPONSE,
      CustomMessageTypes.GROUP_SHIFT_INVITE_REQUEST,
    ].includes(lastMessage.customType as CustomMessageTypes)
  ) {
    const { agentMessage } = parseShiftInviteChatMessage({
      message: lastMessage as EveryMessage,
    });
    return <>{agentMessage}</>;
  }

  if (
    [
      CustomMessageTypes.SHIFT_TIME_PROPOSAL_CREATED,
      CustomMessageTypes.SHIFT_TIME_PROPOSAL_UPDATED,
      CustomMessageTypes.SHIFT_TIME_PROPOSAL_REPLIED,
    ].includes(lastMessage?.customType as CustomMessageTypes)
  ) {
    return <>{shiftTimeProposalDisplayMessage(lastMessage?.customType as CustomMessageTypes)}</>;
  }

  if (
    [
      CustomMessageTypes.WORKER_CREDENTIAL_SHARED,
      CustomMessageTypes.WORKER_CREDENTIAL_PROVISION_REQUEST_COMPLETED,
    ].includes(lastMessage?.customType as CustomMessageTypes)
  ) {
    return <>Shared charting login with you</>;
  }

  if (lastMessage?.customType === CustomMessageTypes.FCM_REQUEST) {
    const data = (lastMessage.data ? JSON.parse(lastMessage.data) : {}) as {
      facilityName?: string;
    };
    return <>{getSendHomeRequestMessage(data.facilityName)}</>;
  }

  function getFromText(): string {
    if (isOutgoingMessage) {
      return "You:";
    }

    if (isWorkplaceGroupChat && isDefined(senderUserId)) {
      const firstName =
        senderProfile?.attributes.firstName ??
        getFirstNameFromNickname(nonAdminLastMessage?.sender?.nickname);

      if (firstName) {
        return `${firstName}:`;
      }
    }

    return "";
  }

  const fromText = getFromText();

  if (lastMessage?.messageType === "file") {
    const fileName = (lastMessage as FileMessage)?.name ?? "";

    return (
      <>
        <CbhIcon size="xSmall" type="document" /> {fromText} {fileName}
      </>
    );
  }

  const messageText = (lastMessage as UserMessage)?.message ?? "";

  return (
    <>
      {fromText} {messageText}
    </>
  );
}
